import { createContext, useContext, useEffect, useMemo, useReducer } from "react";
import type { ReactNode } from "react";
import type { LoggingFlowPayload, LoggingFlowReviewItem } from "./logging-flow";

// Temporary Plate contract (plan todo 11). The plate accumulates items from
// many capture methods (barcode, search, library, quick add — todos 16-18)
// into ONE meal before the unified Meal Review (todo 12) logs it. The plate
// is browser-local ONLY: it must never persist server-side and must never be
// counted in any dashboard query.
//
// Contractual API for todos 12/16/17:
// - PlateItem shape below (unknown nutrition stays undefined, never 0).
// - usePlate() -> { items, totals, addItem, removeItem, updateItem, clear }.
// - plateTotals(items) pure helper (unknown-preserving sums).
// - buildPlateReviewPayload(items) -> startFlow("review", payload) payload.

export const PLATE_STORAGE_KEY = "bitetrack.plate.v1";

// Where a plate item originated, when it came from a saved entity.
export interface PlateItemSourceRef {
  readonly kind: "customFood" | "recipe" | "memory" | "off";
  readonly id: string;
}

export interface PlateItem {
  // Temp id via the Date.now()+Math.random pattern — NEVER crypto.randomUUID
  // (crashes in non-HTTPS WebViews).
  readonly key: string;
  readonly name: string;
  readonly quantity?: number;
  readonly unit?: string;
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly dataSource: string;
  readonly confidence?: string;
  readonly sourceRef?: PlateItemSourceRef;
}

export type NewPlateItem = Omit<PlateItem, "key">;
export type PlateItemPatch = Partial<Omit<PlateItem, "key">>;

export function makePlateKey(): string {
  return `plate_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export interface PlateTotals {
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  // True when at least one item has unknown calories. Macro partiality is
  // observable per nutrient: a total is undefined when NO item defines it.
  readonly hasUnknown: boolean;
}

// Sums only defined values and NEVER zero-fills: a nutrient total is present
// only when at least one item defines it. Mirrors the macro-edit.ts and
// Today.tsx unknown-as-dash conventions.
export function plateTotals(items: readonly PlateItem[]): PlateTotals {
  let calories: number | undefined;
  let proteinG: number | undefined;
  let carbsG: number | undefined;
  let fatG: number | undefined;
  let fiberG: number | undefined;
  let hasUnknown = false;
  for (const item of items) {
    if (item.calories === undefined) {
      hasUnknown = true;
    } else {
      calories = (calories ?? 0) + item.calories;
    }
    if (item.proteinG !== undefined) proteinG = (proteinG ?? 0) + item.proteinG;
    if (item.carbsG !== undefined) carbsG = (carbsG ?? 0) + item.carbsG;
    if (item.fatG !== undefined) fatG = (fatG ?? 0) + item.fatG;
    if (item.fiberG !== undefined) fiberG = (fiberG ?? 0) + item.fiberG;
  }
  return { calories, proteinG, carbsG, fatG, fiberG, hasUnknown };
}

export interface PlateState {
  readonly items: readonly PlateItem[];
}

export const INITIAL_PLATE_STATE: PlateState = { items: [] };

export type PlateAction =
  | { readonly type: "addItem"; readonly item: PlateItem }
  | { readonly type: "removeItem"; readonly key: string }
  | { readonly type: "updateItem"; readonly key: string; readonly patch: PlateItemPatch }
  | { readonly type: "clear" };

// Pure reducer — the testable seam (the repo has no jsdom; interactivity is
// asserted through this reducer and e2e).
export function plateReducer(state: PlateState, action: PlateAction): PlateState {
  switch (action.type) {
    case "addItem":
      return { items: [...state.items, action.item] };
    case "removeItem":
      return { items: state.items.filter((item) => item.key !== action.key) };
    case "updateItem":
      return {
        items: state.items.map((item) =>
          item.key === action.key ? { ...item, ...action.patch, key: item.key } : item,
        ),
      };
    case "clear":
      return INITIAL_PLATE_STATE;
  }
}

// Storage boundary — injectable so node-env tests exercise the exact
// round-trip/corruption logic with a fake. window.localStorage access is
// ALWAYS wrapped in try/catch (WebView-safe).
export interface PlateStorageLike {
  readonly getItem: (key: string) => string | null;
  readonly setItem: (key: string, value: string) => void;
  readonly removeItem: (key: string) => void;
}

function isFiniteNumberOrUndefined(value: unknown): boolean {
  return value === undefined || (typeof value === "number" && Number.isFinite(value));
}

function isStringOrUndefined(value: unknown): boolean {
  return value === undefined || typeof value === "string";
}

const SOURCE_REF_KINDS: readonly string[] = ["customFood", "recipe", "memory", "off"];

export function isPlateItem(value: unknown): value is PlateItem {
  if (typeof value !== "object" || value === null) return false;
  const item = value as Record<string, unknown>;
  if (typeof item.key !== "string" || item.key.length === 0) return false;
  if (typeof item.name !== "string" || item.name.trim().length === 0) return false;
  if (typeof item.dataSource !== "string" || item.dataSource.length === 0) return false;
  if (!isFiniteNumberOrUndefined(item.quantity)) return false;
  if (!isFiniteNumberOrUndefined(item.calories)) return false;
  if (!isFiniteNumberOrUndefined(item.proteinG)) return false;
  if (!isFiniteNumberOrUndefined(item.carbsG)) return false;
  if (!isFiniteNumberOrUndefined(item.fatG)) return false;
  if (!isFiniteNumberOrUndefined(item.fiberG)) return false;
  if (!isStringOrUndefined(item.unit)) return false;
  if (!isStringOrUndefined(item.confidence)) return false;
  if (item.sourceRef !== undefined) {
    if (typeof item.sourceRef !== "object" || item.sourceRef === null) return false;
    const ref = item.sourceRef as Record<string, unknown>;
    if (typeof ref.kind !== "string" || !SOURCE_REF_KINDS.includes(ref.kind)) return false;
    if (typeof ref.id !== "string" || ref.id.length === 0) return false;
  }
  return true;
}

// Rehydrate on mount. Corrupt JSON or a malformed shape is ignored AND
// cleared so the next write starts clean; never throws.
export function readPlateFromStorage(
  storage: PlateStorageLike,
  key: string = PLATE_STORAGE_KEY,
): readonly PlateItem[] {
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch (error: unknown) {
    console.warn("[Frontend][Plate] localStorage read failed; starting with an empty plate", {
      error: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
  if (raw === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    console.warn("[Frontend][Plate] Stored plate is corrupt JSON; clearing it");
    removePlateFromStorage(storage, key);
    return [];
  }
  if (!Array.isArray(parsed) || !parsed.every(isPlateItem)) {
    console.warn("[Frontend][Plate] Stored plate failed validation; clearing it");
    removePlateFromStorage(storage, key);
    return [];
  }
  return parsed;
}

export function writePlateToStorage(
  storage: PlateStorageLike,
  items: readonly PlateItem[],
  key: string = PLATE_STORAGE_KEY,
): void {
  try {
    if (items.length === 0) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, JSON.stringify(items));
    }
  } catch (error: unknown) {
    console.warn("[Frontend][Plate] Plate could not be persisted", {
      itemCount: items.length,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

function removePlateFromStorage(storage: PlateStorageLike, key: string): void {
  try {
    storage.removeItem(key);
  } catch (error: unknown) {
    console.warn("[Frontend][Plate] Stored plate could not be cleared", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

// Maps plate items onto the todo-10 review contract (fields align by name;
// temp keys and sourceRefs stay plate-local). PlateBar and todos 16-17 use
// this for startFlow("review", payload).
export function buildPlateReviewPayload(items: readonly PlateItem[]): LoggingFlowPayload {
  const reviewItems: LoggingFlowReviewItem[] = items.map((item) => ({
    name: item.name,
    ...(item.quantity === undefined ? {} : { quantity: item.quantity }),
    ...(item.unit === undefined ? {} : { unit: item.unit }),
    ...(item.calories === undefined ? {} : { calories: item.calories }),
    ...(item.proteinG === undefined ? {} : { proteinG: item.proteinG }),
    ...(item.carbsG === undefined ? {} : { carbsG: item.carbsG }),
    ...(item.fatG === undefined ? {} : { fatG: item.fatG }),
    ...(item.fiberG === undefined ? {} : { fiberG: item.fiberG }),
    dataSource: item.dataSource,
    ...(item.confidence === undefined ? {} : { confidence: item.confidence }),
  }));
  return { items: reviewItems, plateMode: true };
}

export interface PlateContextValue {
  readonly items: readonly PlateItem[];
  readonly totals: PlateTotals;
  readonly addItem: (item: NewPlateItem) => void;
  readonly removeItem: (key: string) => void;
  readonly updateItem: (key: string, patch: PlateItemPatch) => void;
  readonly clear: () => void;
}

const PlateContext = createContext<PlateContextValue | null>(null);

export interface PlateProviderProps {
  readonly children: ReactNode;
  // Test hook: lets renderToStaticMarkup suites render the plate in a
  // specific state without interaction (mirrors LoggingFlowProvider).
  readonly initialState?: PlateState;
}

function hydratePlate(initialState: PlateState | undefined): PlateState {
  if (initialState !== undefined) return initialState;
  if (typeof window === "undefined") return INITIAL_PLATE_STATE;
  const items = readPlateFromStorage(window.localStorage);
  if (items.length > 0) {
    console.log("[Frontend][Plate] Rehydrated plate from localStorage", { itemCount: items.length });
  }
  return { items };
}

export function PlateProvider({ children, initialState }: PlateProviderProps) {
  const [state, dispatch] = useReducer(plateReducer, initialState, hydratePlate);

  // Browser-local persistence only. Never server-side, never dashboard math.
  useEffect(() => {
    if (typeof window === "undefined") return;
    writePlateToStorage(window.localStorage, state.items);
  }, [state.items]);

  const totals = useMemo(() => plateTotals(state.items), [state.items]);

  const value = useMemo<PlateContextValue>(
    () => ({
      items: state.items,
      totals,
      addItem: (item) => {
        const key = makePlateKey();
        console.log("[Frontend][Plate] Item added", {
          key,
          name: item.name,
          dataSource: item.dataSource,
          itemCount: state.items.length + 1,
        });
        dispatch({ type: "addItem", item: { ...item, key } });
      },
      removeItem: (key) => {
        console.log("[Frontend][Plate] Item removed", { key });
        dispatch({ type: "removeItem", key });
      },
      updateItem: (key, patch) => {
        console.debug("[Frontend][Plate] Item updated", { key, fields: Object.keys(patch) });
        dispatch({ type: "updateItem", key, patch });
      },
      clear: () => {
        console.log("[Frontend][Plate] Plate cleared", { itemCount: state.items.length });
        dispatch({ type: "clear" });
      },
    }),
    [state.items, totals],
  );

  return <PlateContext.Provider value={value}>{children}</PlateContext.Provider>;
}

export function usePlate(): PlateContextValue {
  const context = useContext(PlateContext);
  if (context === null) {
    throw new Error(
      "usePlate must be used within a PlateProvider (mounted in App inside LoggingFlowProvider)",
    );
  }
  return context;
}
